import { useState } from 'react'
import type { AccountTeam } from '../../pages/admin/accountStore'
import type { Activity, Assignment, AssignmentValues, Qualification } from '../../pages/admin/catalogStore'

const FIELD = 'min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small'
const LABEL = 'mb-1 block text-caption font-bold text-ink-muted'

/**
 * An assignment: its duty category, the team it belongs to (or the whole
 * office), its sort priority, an optional required qualification, and the
 * activity it fills in by default. A new one also takes its code and name; once
 * it exists both are shown as they are, because the scheduling functions and the
 * planning board's code palette find an assignment by its name.
 */
export default function AssignmentForm({
  assignment,
  teams,
  activities,
  qualifications,
  saving,
  error,
  onCreate,
  onSave,
  onCancel,
}: {
  assignment?: Assignment
  teams: AccountTeam[]
  activities: Activity[]
  qualifications: Qualification[]
  saving: boolean
  error: string | null
  onCreate: (values: AssignmentValues & { code: string; name: string }) => void
  onSave: (values: AssignmentValues) => void
  onCancel: () => void
}) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [dutyCategory, setDutyCategory] = useState(assignment?.dutyCategory ?? '')
  const [teamId, setTeamId] = useState(assignment?.teamId ?? '')
  const [sortPriority, setSortPriority] = useState(String(assignment?.sortPriority ?? 0))
  const [qualificationId, setQualificationId] = useState(assignment?.requiredQualificationId ?? '')
  const [activityId, setActivityId] = useState(assignment?.defaultActivityId ?? '')

  const priority = sortPriority === '' ? NaN : Number(sortPriority)
  const values: AssignmentValues = {
    dutyCategory: dutyCategory.trim(),
    teamId: teamId || null,
    sortPriority: priority,
    requiredQualificationId: qualificationId || null,
    defaultActivityId: activityId || null,
  }
  const changed =
    !assignment ||
    values.dutyCategory !== assignment.dutyCategory ||
    values.teamId !== assignment.teamId ||
    priority !== assignment.sortPriority ||
    values.requiredQualificationId !== assignment.requiredQualificationId ||
    values.defaultActivityId !== assignment.defaultActivityId
  const canSave =
    values.dutyCategory !== '' &&
    Number.isInteger(priority) &&
    priority >= 0 &&
    changed &&
    (assignment !== undefined || (/^[a-z0-9][a-z0-9-]{0,19}$/.test(code) && name.trim() !== ''))

  // Only an open activity can be chosen, but the one an assignment already has
  // stays listed even if it has since been retired.
  const choices = activities.filter((activity) => activity.active || activity.id === assignment?.defaultActivityId)

  return (
    <form
      className="flex flex-col gap-3 rounded-surface border border-line bg-canvas p-3"
      aria-label={assignment ? `Edit ${assignment.name}` : 'Create assignment'}
      onSubmit={(event) => {
        event.preventDefault()
        if (!canSave || saving) return
        if (assignment) onSave(values)
        else onCreate({ ...values, code, name: name.trim() })
      }}
    >
      {assignment ? (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-small">
          <dt className="text-ink-muted">Name</dt>
          <dd className="font-bold">{assignment.name}</dd>
          <dt className="text-ink-muted">Code</dt>
          <dd className="font-medium">{assignment.code}</dd>
        </dl>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="assignment-name" className={LABEL}>
              Name
            </label>
            <input id="assignment-name" type="text" maxLength={100} value={name} onChange={(event) => setName(event.target.value)} className={FIELD} />
          </div>
          <div>
            <label htmlFor="assignment-code" className={LABEL}>
              Code
            </label>
            <input
              id="assignment-code"
              type="text"
              maxLength={20}
              aria-describedby="assignment-code-hint"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              className={FIELD}
            />
            <p id="assignment-code-hint" className="mt-1 text-caption text-ink-subtle">
              Lowercase letters, digits, and hyphens. It cannot be changed later.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <label htmlFor="assignment-duty" className={LABEL}>
            Duty category
          </label>
          <input id="assignment-duty" type="text" maxLength={100} value={dutyCategory} onChange={(event) => setDutyCategory(event.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="assignment-team" className={LABEL}>
            Team
          </label>
          <select id="assignment-team" value={teamId} onChange={(event) => setTeamId(event.target.value)} className={FIELD}>
            <option value="">Office-wide</option>
            {teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="assignment-priority" className={LABEL}>
            Sort priority
          </label>
          <input id="assignment-priority" type="number" min={0} step={1} value={sortPriority} onChange={(event) => setSortPriority(event.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="assignment-qualification" className={LABEL}>
            Required qualification
          </label>
          <select id="assignment-qualification" value={qualificationId} onChange={(event) => setQualificationId(event.target.value)} className={FIELD}>
            <option value="">None</option>
            {qualifications.map((qualification) => (
              <option key={qualification.id} value={qualification.id}>
                {qualification.name}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="assignment-activity" className={LABEL}>
            Default activity
          </label>
          <select id="assignment-activity" value={activityId} onChange={(event) => setActivityId(event.target.value)} className={FIELD}>
            <option value="">None</option>
            {choices.map((activity) => (
              <option key={activity.id} value={activity.id}>
                {activity.path}
                {activity.active ? '' : ' (retired)'}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <p role="alert" className="text-caption font-semibold text-error">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!canSave || saving}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
        >
          {saving ? 'Saving…' : assignment ? 'Save changes' : 'Create assignment'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex min-h-11 items-center justify-center rounded-control px-4 text-small font-bold text-ink-muted hover:bg-surface-2"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
